import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import Player from '@vimeo/player';
import { trackPDPGalleryClick } from 'utils/tracking';
import { Pause, Play, Unmute, Mute } from '../utils/icon/video-icons';

const Video = ({
  video,
  previousVideo,
  playCurrentVideo,
  autoPlay,
  controls,
  classes = '',
}) => {
  const [player, setPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(true);

  const getVideoId = (id) => id.split('Video/')[1];
  const getEmbeddedUrl = (url) => url.split('/video/')[1];

  const unmuteVideo = () => {
    if (!player) return;
    if (video.host && video.host.toLowerCase() === 'vimeo') {
      player.setVolume(1);
    } else player.muted = false;
    setIsMuted(false);
  };

  const muteVideo = () => {
    if (!player) return;
    if (video.host && video.host.toLowerCase() === 'vimeo') {
      player.setVolume(0);
    } else player.muted = true;
    setIsMuted(true);
  };

  const handleMute = () => (isMuted ? unmuteVideo() : muteVideo());

  const pauseVideo = () => {
    if (!player) return;
    player.pause();
    setIsPlaying(false);
  };

  const playVideo = () => {
    if (!player) return;
    player.play();
    setIsPlaying(true);
    trackPDPGalleryClick('video play');
  };

  const handlePlay = () => (isPlaying ? pauseVideo() : playVideo());

  const initVideo = () => {
    if (video.host && video.host.toLowerCase() === 'vimeo') {
      setPlayer(
        new Player(`video-${getVideoId(video.id)}`, {
          id: getEmbeddedUrl(video.embeddedUrl),
          controls: false,
          autoplay: autoPlay,
          muted: true,
          responsive: true,
          loop: true,
        }),
      );
    } else {
      setPlayer(document.querySelector(`#video-${getVideoId(video.id)} video`));
    }
  };

  useEffect(() => {
    if (!video) return;
    initVideo();
  }, [video]);

  useEffect(() => {
    if (!previousVideo) return;
    if (previousVideo === `video-${getVideoId(video.id)}`) {
      pauseVideo();
      muteVideo();
    }
  }, [previousVideo]);

  useEffect(() => {
    if (!playCurrentVideo) return;
    if (playCurrentVideo === `video-${getVideoId(video.id)}`) {
      playVideo();
    }
  }, [playCurrentVideo]);

  return (
    <div className="relative z-1">
      <div data-video-component id={`video-${getVideoId(video.id)}`}>
        {video.mediaContentType.toLowerCase() === 'video' ? (
          <video
            className={classes}
            autoPlay={autoPlay}
            muted={isMuted}
            loop="true"
          >
            {video.sources.map((source) => (
              <source
                key={source.id}
                src={source.url}
                type={source.mime_type}
                height={source.height}
                width={source.width}
              />
            ))}
          </video>
        ) : null}
      </div>

      {controls && (
        <div className="absolute bottom-0 right-0 z-10 flex">
          <button
            className="text-white"
            type="button"
            aria-label={`${isPlaying ? 'play' : 'pause'}`}
            onClick={handlePlay}
          >
            <span className={`${isPlaying && 'hidden'}`}>
              <Play />
            </span>
            <span className={`${!isPlaying && 'hidden'}`}>
              <Pause />
            </span>
          </button>

          <button
            className="text-white"
            type="button"
            aria-label={`${isMuted ? 'sound-off' : 'sound-on'}`}
            onClick={handleMute}
          >
            <span className={`${isMuted && 'hidden'}`}>
              <Unmute />
            </span>
            <span className={`${!isMuted && 'hidden'}`}>
              <Mute />
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Video;
